import React, { useEffect, useState } from 'react';
import { StanButton, StanToggle } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useOtherSettings } from '../../queries/useOtherSettings';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions';

export const OtherSection: React.FC = () => {
    const { user } = useUser();
    const [showReferralBanner, setShowReferralBanner] = useState(true);
    const [originalShowReferralBanner, setOriginalShowReferralBanner] = useState(true);
    const { mutateAsync: updateOtherSettingsMutation, isPending: isSubmitting } = useOtherSettings();

    // Initialize form data from user
    useEffect(() => {
        if (user) {
            const hideBanner = user.data.hide_store_referral_banner || false;
            setShowReferralBanner(!hideBanner);
            setOriginalShowReferralBanner(!hideBanner);
        }
    }, [user]);

    const isCreatorPro = user?.subscription_tier === SUBSCRIPTION_TIERS.CREATOR_PRO;
    const enableUpdateButton = showReferralBanner !== originalShowReferralBanner && !isSubmitting;

    const handleToggleChange = (checked: boolean) => {
        setShowReferralBanner(checked);
    };

    const handleUpdate = async () => {
        try {
            const updateData = {
                hide_store_referral_banner: !showReferralBanner,
            };
            await updateOtherSettingsMutation(updateData);

            // Analytics
            stanAnalytics('other-settings-update', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
                props: {
                    update_type: 'referral_banner_toggle',
                    hide_store_referral_banner: !showReferralBanner,
                },
            });

            stanNotify({
                type: 'success',
                title: 'Settings Updated!',
                text: 'Your settings have been updated successfully.',
            });

            // Update original value
            setOriginalShowReferralBanner(showReferralBanner);
        } catch (error: any) {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message || 'Please try again later.',
            });
            console.error('Other settings update error:', error);
        }
    };

    return (
        <section className='w-full max-w-3xl space-y-6'>
            <h4>Other</h4>

            <div className='space-y-4'>
                <div className='flex items-center justify-between gap-4'>
                    <div className='flex-1 space-y-3'>
                        <div className='align-center flex items-center justify-start gap-2'>
                            <h5>Stan Store Referral Banner</h5>
                            <StanToggle
                                checked={showReferralBanner}
                                onChange={e => handleToggleChange(e.target.checked)}
                                toggleSize='sm'
                                disabled={!isCreatorPro}
                            />
                        </div>
                        <p className='text-gray text-caption-1-regular'>
                            {isCreatorPro
                                ? "When enabled, you'll receive referral commission from visitors who sign up using this link."
                                : 'Upgrade to Creator Pro to hide the Stan Store Referral Banner.'}
                        </p>
                    </div>
                    <div className='ml-4' />
                </div>
            </div>

            <div className='flex items-center gap-4'>
                <StanButton
                    name='settings-other-update-button'
                    onClick={handleUpdate}
                    disabled={!enableUpdateButton || !isCreatorPro}
                    loading={isSubmitting}
                >
                    Update
                </StanButton>

                {isSubmitting && (
                    <div className='text-caption-1-regular flex items-center'>
                        <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                        Updating...
                    </div>
                )}
            </div>
        </section>
    );
};
